import React, { useState, useEffect } from 'react';
import { UserProfile, MicroClass, Challenge, Badge } from '../types';
import { generateDailyClass } from '../services/geminiService';
import { GraduationCap, Award, CalendarCheck, Clock, CheckCircle2, Trophy, Flame, Play, BookOpen, Activity, ArrowLeft } from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const MicroLearningHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'daily' | 'challenges' | 'badges'>('daily');
  const [dailyClass, setDailyClass] = useState<MicroClass | null>(null);
  const [loadingClass, setLoadingClass] = useState(false);
  const [classStep, setClassStep] = useState<'start' | 'explanation' | 'practice' | 'summary' | 'done'>('start');

  useEffect(() => {
    // Check if we already have a class for today in local state (simulated via user prop for now, normally implies fetch)
    // For this demo, we generate if not recently generated
    if (activeTab === 'daily' && !dailyClass) {
        const today = new Date().toISOString().split('T')[0];
        if (user.microLearning.lastClassDate !== today) {
             loadDailyClass();
        }
    }
  }, [activeTab]);

  const loadDailyClass = async () => {
      setLoadingClass(true);
      try {
          const newClass = await generateDailyClass(user);
          setDailyClass(newClass);
      } catch(e) {
          console.error(e);
      } finally {
          setLoadingClass(false);
      }
  };

  const handleCompleteClass = () => {
      if (!dailyClass) return;
      const today = new Date().toISOString().split('T')[0];
      
      const updatedUser = { ...user };
      updatedUser.microLearning.completedClasses.push(dailyClass.id);
      updatedUser.microLearning.lastClassDate = today;
      updatedUser.xp += 50; // Add XP
      
      // Check for level up
      if (updatedUser.xp >= updatedUser.level * 1000) {
          updatedUser.level += 1;
      }

      onUpdateUser(updatedUser);
      setClassStep('done');
  };

  const handleStartChallenge = (type: 'mindfulness' | 'gratitude' | 'movement' | 'learning') => {
      const titles = {
          mindfulness: 'ذهن‌آگاهی ۷ روزه',
          gratitude: 'معجزه شکرگزاری',
          movement: 'حرکت و نشاط',
          learning: 'یادگیری پرشتاب'
      };
      
      const descs = {
          mindfulness: 'روزی ۳ دقیقه مدیتیشن برای آرامش ذهن.',
          gratitude: 'هر روز ۳ چیز که بابت آن‌ها شکرگزارید را بنویسید.',
          movement: 'روزی ۱۰ دقیقه ورزش سبک برای سلامتی.',
          learning: 'یادگیری ۱ کلمه یا مفهوم جدید در روز.'
      };

      const newChallenge: Challenge = {
          id: Date.now().toString(),
          type,
          title: titles[type],
          description: descs[type],
          durationDays: 7,
          currentDay: 1,
          progress: Array(7).fill(false),
          isActive: true,
          startDate: Date.now()
      };

      onUpdateUser({
          ...user,
          microLearning: {
              ...user.microLearning,
              activeChallenge: newChallenge
          }
      });
  };

  const handleCompleteChallengeDay = () => {
      if (!user.microLearning.activeChallenge) return;
      
      const challenge = { ...user.microLearning.activeChallenge };
      const today = new Date().toISOString().split('T')[0];
      
      if (challenge.lastCompletedDate === today) return; // Already done today

      challenge.progress[challenge.currentDay - 1] = true;
      challenge.lastCompletedDate = today;

      const updatedUser = { ...user };

      if (challenge.currentDay === 7) {
           // Challenge Completed!
           challenge.isActive = false;
           
           const newBadge: Badge = {
               id: Date.now().toString(),
               label: `${challenge.title} - تکمیل`,
               icon: '🏆',
               dateEarned: Date.now(),
               description: `تکمیل موفقیت‌آمیز چالش ${challenge.title}`,
               color: 'bg-yellow-100 text-yellow-600'
           };
           updatedUser.microLearning.badges.push(newBadge);
           updatedUser.xp += 200;
      } else {
           challenge.currentDay += 1;
           updatedUser.xp += 20;
      }

      updatedUser.microLearning.activeChallenge = challenge;
      onUpdateUser(updatedUser);
  };

  const isTodayClassDone = user.microLearning.lastClassDate === new Date().toISOString().split('T')[0];

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="flex items-center justify-between mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <GraduationCap className="w-6 h-6 text-indigo-500" />
             یادگیری خرد
           </h2>
           <p className="text-slate-500 text-sm mt-1">رشد روزانه با کلاس‌های ۵ دقیقه‌ای</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            <button 
                onClick={() => setActiveTab('daily')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'daily' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500'}`}>
                کلاس روز
            </button>
            <button 
                onClick={() => setActiveTab('challenges')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'challenges' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500'}`}>
                چالش‌ها
            </button>
            <button 
                onClick={() => setActiveTab('badges')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'badges' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500'}`}>
                نشان‌ها
            </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
          {/* DAILY CLASS TAB */}
          {activeTab === 'daily' && (
              <div className="max-w-2xl mx-auto">
                  {isTodayClassDone && classStep !== 'done' ? (
                      <div className="bg-white rounded-3xl p-8 text-center border border-slate-100 shadow-sm">
                          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                          <h3 className="text-xl font-bold text-slate-800 mb-2">کلاس امروز تکمیل شد!</h3>
                          <p className="text-slate-500 mb-6">عالی بود! فردا برای یک درس جدید برگرد.</p>
                          <div className="p-4 bg-slate-50 rounded-xl inline-block">
                              <span className="text-slate-600 font-bold">امتیاز فعلی: {user.xp} XP</span>
                          </div>
                      </div>
                  ) : (
                      <>
                        {classStep === 'start' && (
                            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-8 text-white shadow-xl text-center relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                                
                                {loadingClass ? (
                                    <div className="py-12">
                                        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                        <p>در حال آماده‌سازی کلاس اختصاصی شما...</p>
                                    </div>
                                ) : dailyClass ? (
                                    <div className="relative z-10">
                                        <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm mb-6 backdrop-blur-sm">
                                            <Clock className="w-4 h-4" /> ۵ دقیقه
                                        </div>
                                        <h3 className="text-2xl font-bold mb-2">{dailyClass.title}</h3>
                                        <p className="opacity-90 mb-8 text-lg">{dailyClass.topic}</p>
                                        <button 
                                            onClick={() => setClassStep('explanation')}
                                            className="bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-indigo-50 transition-colors shadow-lg flex items-center gap-2 mx-auto"
                                        >
                                            <Play className="w-5 h-5 fill-current" />
                                            شروع کلاس
                                        </button>
                                    </div>
                                ) : (
                                    <p>خطا در بارگذاری کلاس.</p>
                                )}
                            </div>
                        )}

                        {classStep === 'explanation' && dailyClass && (
                             <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-lg animate-slide-in">
                                 <div className="flex items-center gap-2 text-indigo-600 mb-4 font-bold">
                                     <BookOpen className="w-5 h-5" />
                                     بخش ۱: یادگیری (۲ دقیقه)
                                 </div>
                                 <h3 className="text-xl font-bold text-slate-800 mb-4">{dailyClass.title}</h3>
                                 <p className="text-slate-600 leading-loose text-lg mb-8">
                                     {dailyClass.content.explanation}
                                 </p>
                                 <button onClick={() => setClassStep('practice')} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors">
                                     بعدی: تمرین عملی
                                 </button>
                             </div>
                        )}

                        {classStep === 'practice' && dailyClass && (
                             <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-lg animate-slide-in">
                                 <div className="flex items-center gap-2 text-amber-600 mb-4 font-bold">
                                     <Activity className="w-5 h-5" />
                                     بخش ۲: تمرین (۲ دقیقه)
                                 </div>
                                 <div className="bg-amber-50 p-6 rounded-2xl border border-amber-100 mb-8">
                                     <p className="text-slate-800 font-medium text-lg text-center leading-relaxed">
                                         {dailyClass.content.practice}
                                     </p>
                                 </div>
                                 <button onClick={() => setClassStep('summary')} className="w-full bg-amber-500 text-white py-3 rounded-xl font-bold hover:bg-amber-600 transition-colors">
                                     بعدی: جمع‌بندی
                                 </button>
                             </div>
                        )}

                        {classStep === 'summary' && dailyClass && (
                             <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-lg animate-slide-in">
                                 <div className="flex items-center gap-2 text-emerald-600 mb-4 font-bold">
                                     <CheckCircle2 className="w-5 h-5" />
                                     بخش ۳: جمع‌بندی (۱ دقیقه)
                                 </div>
                                 <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100 mb-8 text-center">
                                     <p className="text-emerald-800 font-bold text-xl leading-relaxed">
                                         "{dailyClass.content.summary}"
                                     </p>
                                 </div>
                                 <button onClick={handleCompleteClass} className="w-full bg-emerald-500 text-white py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors">
                                     تکمیل کلاس و دریافت امتیاز
                                 </button>
                             </div>
                        )}

                         {classStep === 'done' && (
                             <div className="text-center animate-fade-in pt-10">
                                 <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                     <Trophy className="w-12 h-12 text-yellow-500" />
                                 </div>
                                 <h3 className="text-2xl font-bold text-slate-800 mb-2">+50 XP</h3>
                                 <p className="text-slate-500 mb-8">تبریک! یک قدم به نسخه بهتر خودت نزدیک‌تر شدی.</p>
                                 <button onClick={() => { setActiveTab('daily'); setClassStep('start'); }} className="bg-slate-100 text-slate-600 px-6 py-2 rounded-xl font-medium hover:bg-slate-200">
                                     بازگشت
                                 </button>
                             </div>
                        )}
                      </>
                  )}
              </div>
          )}

          {/* CHALLENGES TAB */}
          {activeTab === 'challenges' && (
              <div className="space-y-6">
                  {user.microLearning.activeChallenge && user.microLearning.activeChallenge.isActive ? (
                      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
                          <div className="flex justify-between items-center mb-4">
                              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                                  <Flame className="w-5 h-5 text-orange-500" />
                                  چالش فعال: {user.microLearning.activeChallenge.title}
                              </h3>
                              <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full font-bold">
                                  روز {user.microLearning.activeChallenge.currentDay} از {user.microLearning.activeChallenge.durationDays}
                              </span>
                          </div>
                          <p className="text-slate-600 mb-6 text-sm">{user.microLearning.activeChallenge.description}</p>
                          
                          {/* Progress Bar */}
                          <div className="flex gap-1 mb-6">
                              {user.microLearning.activeChallenge.progress.map((done, idx) => (
                                  <div key={idx} className={`h-2 flex-1 rounded-full ${done ? 'bg-orange-500' : 'bg-slate-100'}`}></div>
                              ))}
                          </div>

                          <button 
                             onClick={handleCompleteChallengeDay}
                             disabled={user.microLearning.activeChallenge.lastCompletedDate === new Date().toISOString().split('T')[0]}
                             className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 transition-colors"
                          >
                              {user.microLearning.activeChallenge.lastCompletedDate === new Date().toISOString().split('T')[0] ? 'امروز انجام شد ✅' : 'تکمیل فعالیت امروز'}
                          </button>
                      </div>
                  ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                              { id: 'mindfulness', title: 'ذهن‌آگاهی', icon: '🧘‍♂️', color: 'bg-teal-50 text-teal-600' },
                              { id: 'gratitude', title: 'شکرگزاری', icon: '🙏', color: 'bg-pink-50 text-pink-600' },
                              { id: 'movement', title: 'حرکت', icon: '🏃‍♂️', color: 'bg-blue-50 text-blue-600' },
                              { id: 'learning', title: 'یادگیری', icon: '📚', color: 'bg-indigo-50 text-indigo-600' },
                          ].map((c) => (
                              <button 
                                key={c.id} 
                                onClick={() => handleStartChallenge(c.id as any)}
                                className={`p-6 rounded-2xl text-right border border-slate-100 hover:shadow-md transition-all ${c.color} bg-opacity-50`}
                              >
                                  <div className="text-3xl mb-2">{c.icon}</div>
                                  <h4 className="font-bold text-lg mb-1">{c.title}</h4>
                                  <p className="text-sm opacity-80">چالش ۷ روزه</p>
                              </button>
                          ))}
                      </div>
                  )}
              </div>
          )}

          {/* BADGES TAB */}
          {activeTab === 'badges' && (
              <div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {user.microLearning.badges.length === 0 ? (
                          <div className="col-span-full text-center py-20 text-slate-400">
                              <Award className="w-12 h-12 mx-auto mb-4 opacity-20" />
                              <p>هنوز نشانی دریافت نکرده‌اید. چالش‌ها را کامل کنید!</p>
                          </div>
                      ) : (
                          user.microLearning.badges.map(badge => (
                              <div key={badge.id} className={`p-4 rounded-2xl flex flex-col items-center text-center gap-2 ${badge.color || 'bg-slate-50'}`}>
                                  <div className="text-4xl">{badge.icon}</div>
                                  <span className="font-bold text-sm">{badge.label}</span>
                                  <span className="text-[10px] opacity-70">{new Date(badge.dateEarned).toLocaleDateString('fa-IR')}</span>
                              </div>
                          ))
                      )}
                  </div>
              </div>
          )}
      </div>
    </div>
  );
};

export default MicroLearningHub;
